import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { LogIn } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { FormField } from '@/components/forms/form-field';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { DEFAULT_LOGO_URL } from '@/components/layout/brand';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { login } from '@/features/auth/auth-api';
import { useAuth } from '@/hooks/use-auth';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/api-client';
import { errorMessage } from '@/lib/error-message';
import { emailSchema } from '@/lib/validation';

const schema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Informe a senha'),
});
type FormValues = z.infer<typeof schema>;

export function LoginPage() {
  useDocumentTitle('Entrar');
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/dashboard';

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });
  const mutation = useMutation({
    mutationFn: (values: FormValues) => login(values),
    onSuccess: () => void navigate(from, { replace: true }),
  });

  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  const errorText = mutation.error
    ? ApiError.is(mutation.error, 'AUTH_ACCOUNT_LOCKED')
      ? mutation.error.message
      : errorMessage(mutation.error, 'Não foi possível entrar')
    : null;

  return (
    <div className="flex min-h-svh flex-col bg-background">
      <main className="flex flex-1 items-center justify-center px-4 py-10 sm:px-6">
        <div className="w-full max-w-sm space-y-6 rounded-xl border bg-card p-6 sm:p-8">
          <div className="flex flex-col items-center gap-4 text-center">
            <img
              src={DEFAULT_LOGO_URL}
              alt="LD Embalagens"
              className="h-20 w-auto object-contain"
            />
            <div className="space-y-1">
              <h1 className="text-xl font-semibold tracking-tight">Entrar no LD Gestão</h1>
              <p className="text-sm text-muted-foreground">Use seu e-mail e senha corporativos.</p>
            </div>
          </div>

          {errorText ? (
            <Alert variant="destructive">
              <AlertDescription>{errorText}</AlertDescription>
            </Alert>
          ) : null}

          <form
            className="space-y-4"
            onSubmit={form.handleSubmit((v) => mutation.mutate(v))}
            noValidate
          >
            <FormField
              id="email"
              label="E-mail"
              required
              error={form.formState.errors.email?.message}
            >
              <Input
                id="email"
                type="email"
                autoComplete="username"
                inputMode="email"
                autoFocus
                placeholder="voce@empresa.com.br"
                {...form.register('email')}
                aria-invalid={Boolean(form.formState.errors.email)}
              />
            </FormField>
            <FormField
              id="password"
              label="Senha"
              required
              error={form.formState.errors.password?.message}
            >
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                {...form.register('password')}
                aria-invalid={Boolean(form.formState.errors.password)}
              />
            </FormField>
            <Button type="submit" size="lg" className="w-full" loading={mutation.isPending}>
              <LogIn />
              Entrar
            </Button>
          </form>
          <p className="text-center text-xs text-muted-foreground">
            Esqueceu a senha? Solicite a redefinição ao administrador da sua empresa.
          </p>
        </div>
      </main>
      <footer className="pb-safe px-4 py-4 text-center text-xs text-muted-foreground">
        © {new Date().getFullYear()} LD Gestão · ERP industrial da LD Embalagens
      </footer>
    </div>
  );
}
